import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Plus, Search, FileText } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["draft", "sent", "accepted", "rejected", "expired", "revised"];

export default function Quotes() {
  const { t, fmtCurrency, fmtDate } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["quotes", q, status, page],
    queryFn: async () => (await api.get("/quotes", { params: { q, status: status === "all" ? "" : status, page, page_size: pageSize } })).data,
    placeholderData: keepPreviousData,
  });
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div>
      <PageHeader
        title={t("quotes.title")}
        subtitle={t("quotes.subtitle")}
        actions={can("quotes:write") && (
          <Button onClick={() => navigate("/quotes/new")} data-testid="new-quote-button"><Plus className="h-4 w-4" />{t("quotes.new")}</Button>
        )}
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={t("common.search")} className="pl-9" data-testid="quote-search-input" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-48" data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={FileText} title={t("quotes.empty")} testId="quotes-empty"
            action={can("quotes:write") && <Button onClick={() => navigate("/quotes/new")}><Plus className="h-4 w-4" />{t("quotes.new")}</Button>} />
        ) : (
          <Table data-testid="quotes-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("quotes.number")}</TableHead>
                <TableHead>{t("quotes.customer")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("quotes.valid_until")}</TableHead>
                <TableHead className="text-right">{t("quotes.total")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((qu) => (
                <TableRow key={qu.id} className="cursor-pointer" onClick={() => navigate(`/quotes/${qu.id}`)} data-testid={`quote-row-${qu.id}`}>
                  <TableCell className="font-medium tabular-nums">{qu.quote_number}<span className="ml-1 text-xs text-slate-400">v{qu.version}</span></TableCell>
                  <TableCell>{qu.customer_name}</TableCell>
                  <TableCell className="hidden text-slate-500 md:table-cell">{qu.valid_until ? fmtDate(qu.valid_until) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(qu.total)}</TableCell>
                  <TableCell><StatusBadge status={qu.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {data && data.total > pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{t("common.total")}: {data.total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => p - 1)} data-testid="quotes-prev">{t("common.previous")}</Button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)} data-testid="quotes-next">{t("common.next")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
